import { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Settings as SettingsIcon, Save, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function Settings({ user, onLogout }) {
  const [settings, setSettings] = useState({
    weekly_days_off: ['Saturday', 'Sunday'],
    half_day_exceed_threshold: 4,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings({
        weekly_days_off: response.data.weekly_days_off || ['Saturday', 'Sunday'],
        half_day_exceed_threshold: response.data.half_day_exceed_threshold || 4,
      });
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleDayToggle = (day) => {
    setSettings(prev => ({
      ...prev,
      weekly_days_off: prev.weekly_days_off.includes(day)
        ? prev.weekly_days_off.filter(d => d !== day)
        : [...prev.weekly_days_off, day]
    }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar user={user} onLogout={onLogout} />
        <div className="flex-1 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="flex-1 md:ml-0">
        {/* Header */}
        <div className="bg-white border-b border-border px-4 md:px-8 py-4 md:py-6 pt-16 md:pt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <SettingsIcon className="w-5 h-5 md:w-6 md:h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Settings</h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">Configure company leave policies</p>
              </div>
            </div>
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-4 md:p-8 max-w-4xl">
          {/* Weekly Days Off */}
          <div className="bg-white rounded-xl border border-border p-6 shadow-sm mb-6">
            <h2 className="text-lg font-heading font-semibold text-foreground mb-2">Weekly Days Off</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Select the days that should be excluded from leave calculations (e.g., weekends).
            </p>
            <div className="flex flex-wrap gap-2">
              {DAYS_OF_WEEK.map((day) => (
                <button
                  key={day}
                  onClick={() => handleDayToggle(day)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    settings.weekly_days_off.includes(day)
                      ? 'bg-primary text-white'
                      : 'bg-muted text-foreground hover:bg-muted/70'
                  }`}
                >
                  {day}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Currently selected: {settings.weekly_days_off.length > 0 ? settings.weekly_days_off.join(', ') : 'None'}
            </p>
          </div>

          {/* Half Day Threshold */}
          <div className="bg-white rounded-xl border border-border p-6 shadow-sm mb-6">
            <h2 className="text-lg font-heading font-semibold text-foreground mb-2">Half Day Exceed Threshold</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Maximum number of half days an employee can take per year before receiving a warning notification.
            </p>
            <div className="flex items-center gap-4">
              <input
                type="number"
                min="1"
                max="50"
                value={settings.half_day_exceed_threshold}
                onChange={(e) => setSettings({ ...settings, half_day_exceed_threshold: parseInt(e.target.value) || 4 })}
                className="w-24 px-4 py-2 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <span className="text-sm text-muted-foreground">half days per year</span>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-blue-800 mb-1">How it works</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• <strong>Weekly Days Off:</strong> These days will be excluded when calculating leave duration.</li>
              <li>• <strong>Half Day Threshold:</strong> When exceeded, a red notification dot will appear on leave requests in the dashboard.</li>
              <li>• Employees will also see a warning when applying for leave if they&apos;ve exceeded the threshold.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
